"use client";
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useOptimistic,
  useRef,
  useState,
  useTransition,
} from "react";
import { type Cart as CartData } from "@cappelendamm/e-commerce-services";
import * as actions from "./actions";
import { type ECommerceItemLocation } from "@/tracking/e-commerce-events";
import { trackCartEvent } from "./tracker";
import { CartToast } from "./CartToast";
import { Cart as CartComponent } from "./Cart";
import { optimisticCartReducer } from "./optimistic-cart";

export const CartContext = createContext<{
  cart: CartData | null;
  isPending: boolean;
  cartOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
  increaseQuantity: (event: {
    sku: string;
    quantity?: number;
    location: ECommerceItemLocation;
  }) => Promise<void>;
  decreaseQuantity: (event: {
    sku: string;
    quantity?: number;
    location: ECommerceItemLocation;
  }) => Promise<void>;
  removeItem: (event: {
    sku: string;
    location: ECommerceItemLocation;
  }) => Promise<void>;
  addItem: (event: {
    sku: string;
    name: string;
    price: number;
    location: ECommerceItemLocation;
  }) => Promise<void>;
  clearCart: () => Promise<void>;
  setFreight: (event: {
    isSvalbardOrJanMayen?: boolean;
    location: ECommerceItemLocation;
  }) => Promise<void>;
}>({
  cart: null,
  isPending: true,
  cartOpen: false,
  openCart: () => {},
  closeCart: () => {},
  increaseQuantity: async (event) => {
    await actions.increaseQuantity(event);
  },
  decreaseQuantity: async (event) => {
    await actions.decreaseQuantity(event);
  },
  addItem: async (event) => {
    await actions.addItem(event);
  },
  removeItem: async (event) => {
    await actions.removeItem(event);
  },
  clearCart: async () => {
    await actions.clearCart();
  },
  setFreight: async () => {
    await actions.setFreight({});
  },
});

export const INITIAL_CART: CartData = {
  id: "initial",
  items: [],
  state: "cart",
  total: {
    gross: 0,
    currency: "NOK",
    net: 0,
    taxAmount: 0,
  },
  updatedAt: "initial",
} as const;

// @TODO: Remove the use of React.StartTransition when this bug is resolved: https://github.com/facebook/react/issues/28574
export function CartProvider({
  children,
  experiments,
}: {
  children: ReactNode;
  experiments?: {
    vippsInNetsForUnitSale: boolean;
  };
}) {
  const [cartData, setCartData] = useState<CartData & { isPending: boolean }>({
    ...INITIAL_CART,
    isPending: false,
  });
  const [optimisticCart, setOptimisticCart] = useOptimistic(
    cartData,
    optimisticCartReducer,
  );
  const [isPending, startTransition] = useTransition();
  const [cartOpen, setCartOpen] = useState<boolean>(false);
  const cartRef = useRef<HTMLDialogElement | null>(null);

  useEffect(() => {
    const initialLoad = async () => {
      const data = await actions.getCart();
      if (data) setCartData({ ...data, isPending: false });
    };
    initialLoad();
  }, []);

  const increaseQuantity = async ({
    sku,
    quantity = 1,
    location,
  }: {
    sku: string;
    quantity?: number;
    location: ECommerceItemLocation;
  }) => {
    startTransition(() => {
      setOptimisticCart({
        type: "increase-quantity",
        sku,
        quantity,
      });
    });
    const data = await actions.increaseQuantity({ sku, quantity });
    if (data) {
      React.startTransition(() => setCartData({ ...data, isPending: false }));
      trackCartEvent(data, "add_to_cart", location);
    }
  };

  const decreaseQuantity = async ({
    sku,
    quantity = 1,
    location,
  }: {
    sku: string;
    quantity?: number;
    location: ECommerceItemLocation;
  }) => {
    startTransition(() =>
      setOptimisticCart({
        type: "decrease-quantity",
        sku,
        quantity,
      }),
    );
    const data = await actions.decreaseQuantity({ sku, quantity });
    if (data) {
      React.startTransition(() => setCartData({ ...data, isPending: false }));
      trackCartEvent(data, "remove_from_cart", location);
    }
  };

  const removeItem = async ({
    sku,
    location,
  }: {
    sku: string;
    location: ECommerceItemLocation;
  }) => {
    startTransition(() =>
      setOptimisticCart({
        type: "remove-item",
        sku,
      }),
    );
    const data = await actions.removeItem({ sku });
    if (data) {
      React.startTransition(() => setCartData({ ...data, isPending: false }));
      trackCartEvent(data, "remove_from_cart", location);
    }
  };

  const addItem = async ({
    sku,
    name,
    price,
    location,
  }: {
    sku: string;
    name: string;
    price: number;
    location: ECommerceItemLocation;
  }) => {
    startTransition(() =>
      setOptimisticCart({
        type: "add-item",
        sku,
        name,
        price,
      }),
    );
    const data = await actions.addItem({ sku });
    if (data) {
      React.startTransition(() => setCartData({ ...data, isPending: false }));
      trackCartEvent(data, "add_to_cart", location);
    }
  };

  const clearCart = useCallback(async () => {
    await actions.clearCart();
    setCartData({ ...INITIAL_CART, isPending: false });
  }, []);

  const openCart = useCallback(() => {
    if (cartRef.current) {
      cartRef.current?.showModal();
      setCartOpen(true);
    }
  }, [setCartOpen]);

  const closeCart = useCallback(() => {
    if (cartRef.current) {
      cartRef.current?.close();
      setCartOpen(false);
    }
  }, [setCartOpen]);

  const setFreight = useCallback(
    async ({
      isSvalbardOrJanMayen,
      location,
    }: {
      isSvalbardOrJanMayen?: boolean;
      location: ECommerceItemLocation;
    }) => {
      const data = await actions.setFreight({ isSvalbardOrJanMayen });
      if (data) {
        React.startTransition(() => setCartData({ ...data, isPending: false }));
        trackCartEvent(data, "add_to_cart", location);
      }
    },
    [],
  );

  return (
    <CartContext
      value={{
        cart: optimisticCart,
        isPending: optimisticCart.isPending || isPending,
        cartOpen,
        openCart,
        closeCart,
        addItem,
        removeItem,
        decreaseQuantity,
        increaseQuantity,
        clearCart,
        setFreight,
      }}
    >
      {children}
      <CartToast />
      <CartComponent
        ref={cartRef}
        vippsInNetsForUnitSale={experiments?.vippsInNetsForUnitSale ?? false}
      />
    </CartContext>
  );
}

export const useCart = () => useContext(CartContext);
